import { startWixConnect } from '../utils/wixConnect';
import { useState, useEffect } from 'react';
import WebflowSitePanel from './WebflowSitePanel';
import { startWebflowConnect } from '../utils/webflowConnect';
import { Modal, Box, useMediaQuery, Button, FormControlLabel, Switch, IconButton } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faClone, faCircleCheck, faTimesCircle, faCalendarCheck, faArrowDown, faHeadset, faXmark, faRotate } from '@fortawesome/free-solid-svg-icons';
import './integrationModal.css';
import { useTranslation } from 'react-i18next';
import { useSelectedBot } from '../contexts/SelectedBotContext';

const IntegrationModal = ({ open, onClose, botId }) => {
  const { t: tr } = useTranslation();
  const { bots: knownBots = [] } = useSelectedBot() || {};
  // An agent on a Webflow site runs through the Ultimo Bots app: no snippet
  // to paste (Webflow: code goes through the Custom Code API).
  const webflowHostedAgent = !!knownBots.find((b) => String(b.id) === String(botId))?.webflow_hosted;
  const [copied, setCopied] = useState(false);
  const [tab, setTab] = useState(0);
  const [webflowStatus, setWebflowStatus] = useState(null);
  const [wixStatus, setWixStatus] = useState(null);
  const [wixProduct, setWixProduct] = useState(null);
  const [wixUrl, setWixUrl] = useState(null);
  const [site, setSite] = useState(null);
  const [sites, setSites] = useState([]);
  const [loading, setLoading] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [copiedId, setCopiedId] = useState(false);

  useEffect(() => {
    if (!open) {
      setCopied(false);
      setTab(0);
      setWebflowStatus(null);
      setSite(null);
      setSites([]);
    }
  }, [open]);

  useEffect(() => {
    if (tab === 1 && botId) fetchWixStatus();
  }, [tab]);

  useEffect(() => {
    if (tab === 2 && botId) fetchWebflowStatus();
  }, [tab]);

  // From webflow/status: live | pending | off_in_page | off, and the site row.
  const [webflowState, setWebflowState] = useState(null);
  const [webflowSiteRow, setWebflowSiteRow] = useState(null);
  const [webflowDisconnected, setWebflowDisconnected] = useState(null);

  const fetchWebflowStatus = async () => {
    setLoading(true);
    try {
      const res = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}webflow/status?bot_id=${botId}`
      );
      const data = await res.json();
      setWebflowStatus(data.status);
      setWebflowState(data.state || null);
      const row = data.site || data.last_site || null;
      setWebflowSiteRow(row);

      if (data.sites?.length) {
        setSites(data.sites);
        const pick = (row && data.sites.find((s) => s.id === row.id)) || row || data.sites[0];
        setSite({ id: pick.id, name: pick.name });
      } else {
        setSites([]);
        setSite(null);
      }
    } catch (err) {
      console.error('Failed to fetch Webflow status:', err);
      setWebflowStatus('error');
    } finally {
      setLoading(false);
    }
  };

  const fetchWixStatus = async () => {
    setLoading(true);
    try {
      const res = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}wix/status?bot_id=${botId}`
      );
      const data = await res.json();
      if (!res.ok) throw new Error('Could not check Wix');
      setWixStatus(data.status);
      setWixProduct(data.wix_product || null);
      setWixUrl(data.url)

    } catch (err) {
      console.error('Failed to fetch Wix status:', err);
      setWixStatus('error');
    }
    setLoading(false);
  };

  const integrationSnippet =
`<div id="chat-widget-container" data-user-id="${botId}"></div>
<script src="https://robert-kloepsch.github.io/ultimo-bots-widget/dist/bundle.js" defer></script>
<a href="https://www.ultimo-bots.com" target="_blank" rel="noopener" style="display: none;">Our website uses intelligent chatbots powered by Ultimo Bots to improve customer service.</a>`;

  const handleCopyToClipboard = () => {
    navigator.clipboard
      .writeText(integrationSnippet)
      .then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      })
      .catch((e) => console.error('Failed to copy snippet:', e));
  };

  const handleWebflowConnect = async () => {
    try {
      // The OAuth state is minted server-side and bound to this session.
      const res = await startWebflowConnect({ botId });
      if (res?.reconnected) await fetchWebflowStatus();
    } catch (err) {
      console.error('Webflow connect failed:', err);
      alert('Could not start the Webflow connection. Please try again.');
    }
  };

  const handleActivateWebflowChatbot = async () => {
    try {
      setLoading(true);
      const res = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}webflow/activate_chatbot?bot_id=${botId}&site_id=${site.id}`,
        { method: 'POST', headers: { 'Content-Type': 'application/json' } }
      );
      if (res.status === 429) {
        const data = await res.json();
        alert(data.message || 'Webflow rate limit reached. Please try again in 1 minute.');
      } else if (res.ok) {
        await fetchWebflowStatus();
      }
    } catch (err) {
      console.error('Integration failed:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleDeactivateWebflowChatbot = async () => {
    try {
      setLoading(true);
      const res = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}webflow/deactivate_chatbot?bot_id=${botId}&site_id=${site.id}`,
        { method: 'POST', headers: { 'Content-Type': 'application/json' } }
      );
      if (res.status === 429) {
        const data = await res.json();
        alert(data.message || 'Webflow rate limit reached. Please try again in 1 minute.');
      } else if (res.ok) {
        await fetchWebflowStatus();
      }
    } catch (err) {
      console.error('Failed to remove chatbot:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveWebflow = async () => {
    try {
      setLoading(true);
      const res = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}webflow/remove_webflow?bot_id=${botId}`,
        { method: 'POST', headers: { 'Content-Type': 'application/json' } }
      );
      if (res.ok) {
        const d = await res.json().catch(() => ({}));
        setWebflowDisconnected(Array.isArray(d.removed_sites) ? d.removed_sites : []);
        await fetchWebflowStatus();
      }
    } catch (err) {
      console.error('Failed to remove chatbot:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleActivateWixChatbot = async () => {
    try {
      setLoading(true);
      const r = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}wix/activate_chatbot?bot_id=${botId}`,
        { method: 'POST' }
      );
      if (r.ok) {
        await fetchWixStatus();
      } else {
        console.error('Activation failed:', await r.text());
      }
    } catch (err) {
      console.error('Failed to activate Wix chatbot:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleDeactivateWixChatbot = async () => {
    try {
      setLoading(true);
      const r = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}wix/deactivate_chatbot?bot_id=${botId}`,
        { method: 'POST' }
      );
      if (r.ok) {
        await fetchWixStatus();
      } else {
        console.error('Deactivation failed:', await r.text());
      }
    } catch (err) {
      console.error('Failed to deactivate Wix chatbot:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCopyBotId = () => {
    navigator.clipboard
      .writeText(botId)
      .then(() => {
        setCopiedId(true);
        setTimeout(() => setCopiedId(false), 2000);
      })
      .catch((e) => console.error('Failed to copy Bot ID:', e));
  };

  const tabButtons = [
    { label: 'Snippet' },
    {
      label: (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
          <img
            src="/wix-logo.png"
            alt="Webflow logo"
            style={{ width: '34px', height: '14px' }}
          />
        </span>
      )
    },
    {
      label: (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
          <img
            src="/webflow-logo.png"
            alt="Webflow logo"
            style={{ width: '85px', height: '15px' }}
          />
        </span>
      )
    },
    {
      label: (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
          <img
            src="/wordpress-logo.png"
            alt="Webflow logo"
            style={{ width: '90px', height: '23px' }}
          />
        </span>
      )
    },
  ];

  return (
    <Modal
      open={open}
      onClose={(event, reason) => {
        if (reason === "backdropClick" || reason === "escapeKeyDown") return;
        onClose();
      }}>
      <Box
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          bgcolor: 'white',
          borderRadius: '10px',
          boxShadow: 24,
          p: isMobile ? 2 : 4,
          width: isMobile ? '80%' : '90%',
          maxWidth: 600,
          maxHeight: '80vh',
          outline: 'none',
          overflow: 'auto'
        }}
      >
        <div className="integration-modal">
          <IconButton
            onClick={onClose}
            sx={{
              position: 'absolute',
              top: 8,
              right: 8,
              color: 'text.secondary',
              '&:hover': { color: 'text.primary' },
            }}
            aria-label="close tutorial"
          >
            <FontAwesomeIcon icon={faXmark} />
          </IconButton>
          <>
            <h2>Integrate Your Chatbot</h2>

            <div className="tabs">
              {tabButtons.map((t, i) => (
                <button
                  key={i}
                  className={`tab ${tab === i ? 'active' : ''}`}
                  onClick={() => setTab(i)}
                >
                  {t.label}
                </button>
              ))}
            </div>

            {tab === 0 && webflowHostedAgent && (
              <p className="integration-connect-text" data-testid="wf-no-snippet">{tr('wf_no_snippet_for_webflow')}</p>
            )}
            {tab === 0 && !webflowHostedAgent && (
              <>
                <p style={{margin: '0'}}>
                  <strong>To integrate your chatbot, copy the entire snippet below and paste it inside the <code>&lt;body&gt;</code> tag of your website’s HTML.</strong>
                </p>
                <div className='arrow-down-container'>
                  <FontAwesomeIcon icon={faArrowDown}/>
                </div>
                <div className="code-wrapper">
                  <button className="copy-btn" onClick={handleCopyToClipboard}>
                    <div className="copy-button-content">
                      <FontAwesomeIcon icon={copied ? faCircleCheck : faClone} />
                      {copied ? 'Copied!' : 'Copy'}
                    </div>
                  </button>
                  <div className="code-container">
                    <pre>
                      <code>{integrationSnippet}</code>
                    </pre>
                  </div>
                  <div className="integration-options">
                    <div className='integration-support-header-container'>
                      <FontAwesomeIcon className="integratin-support-icon" icon={faHeadset}/>
                      <h3 style={{margin: '0'}} >Need help with integration?</h3>
                    </div>
                      <div className="option-item" style={{margin: '0'}}>
                        <p style={{margin: '0', marginBottom: '10px'}}>Book a <strong>free 15-minute integration</strong> call where we guide you step by step to integrate your chatbot.</p>
                        <button
                          className="integration-option-button"
                          onClick={() => window.open('https://calendly.com/build-ultimo-bots/integration_support', '_blank')}
                        >
                          <FontAwesomeIcon className="integratin-button-icon" icon={faCalendarCheck} />
                          Book integration call
                        </button>
                      </div>
                  </div>
                </div>
              </>
            )}

            {tab === 1 && (
              <div className="wix-tab">
                {loading ? (
                  <div className="integration-loading-container">
                    <div className="integration-spinner"></div>
                  </div>
                ) : wixStatus === 'error' ? (<div role="alert"><p>Could not check your Wix connection.</p><button onClick={fetchWixStatus}>Retry</button></div>) : (!wixUrl && (wixStatus === "token_with_code_disabled" || wixStatus === "token_with_code")) ? (
                  <>
                      <p>The Wix page connected to this chatbot is either not published or currently unavailable. Please publish your site and try again, or connect the chatbot to a different site using the 'Connect' button below.</p>                    <Button
                      variant="contained"
                      onClick={() => {
                        startWixConnect(botId, { onConnected: fetchWixStatus });
                      }}
                      sx={{
                        backgroundColor: '#5616ea',
                        '&:hover': { backgroundColor: '#4a14d6' },
                      }}
                    >
                      Connect
                    </Button>
                  </>
                ) : ['no_token', 'uninstalled'].includes(wixStatus) || (wixProduct && wixProduct.status !== 'installed') ? (
                  <>
                    <p>Use the official Ultimo Bots plugin for <strong>Wix</strong> to add the chat widget to your website.</p>
                    <Button
                      variant="contained"
                      onClick={() => {
                        startWixConnect(botId, { onConnected: fetchWixStatus });
                      }}
                      sx={{
                        backgroundColor: '#5616ea',
                        '&:hover': { backgroundColor: '#4a14d6' },
                      }}
                    >
                      Connect
                    </Button>
                  </>
                ) : (
                  <Box className="control-group">
                    <div className='wix-status-container'>
                      <div>
                        <h3 className="wix-status-title">Wix Connection</h3>
                        <p className="wix-status-line">
                          <FontAwesomeIcon icon={faCircleCheck} className="wix-icon connected" />
                          Your chatbot is connected to this Wix site:&nbsp;
                          <a href={wixUrl} target="_blank" rel="noopener noreferrer" className="wix-url">{wixUrl}</a>
                        </p>
                        <Button
                          variant="contained"
                          onClick={() => {
                            startWixConnect(botId, { action: 'change', onConnected: fetchWixStatus });
                          }}
                          sx={{
                            p: 0.3,
                            paddingRight: 1,
                            paddingLeft: 1,
                            backgroundColor: '#5616ea',
                            '&:hover': { backgroundColor: '#4a14d6' },
                          }}
                        >
                          <div className='change-wix-button-content'>
                            <FontAwesomeIcon icon={faRotate} className="wix-icon-change" />
                            Change
                          </div>
                        </Button>
                      </div>

                      <div>
                        <h3 className="wix-status-title">Chatbot Integration</h3>
                        <p className="wix-status-line">
                          <FontAwesomeIcon
                            icon={wixStatus === 'token_with_code' ? faCircleCheck : faTimesCircle}
                            className={`wix-icon ${wixStatus === 'token_with_code' ? 'live' : 'not-live'}`}
                          />
                          Your chatbot is&nbsp;
                          <span className={`wix-status-chip ${wixStatus === 'token_with_code' ? 'chip-live' : 'chip-not-live'}`}>
                            {wixStatus === 'token_with_code' ? 'Live' : 'Not Live'}
                          </span>
                        </p>
                      </div>
                    </div>

                    <FormControlLabel
                      control={
                        <Switch
                          checked={wixStatus === 'token_with_code'}
                          onChange={wixStatus === 'token_with_code' ? handleDeactivateWixChatbot : handleActivateWixChatbot}
                          sx={{
                            '& .MuiSwitch-switchBase.Mui-checked': { color: '#5616ea' },
                            '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#5616ea' },
                            '& .MuiSwitch-track': { backgroundColor: '#ccc' },
                          }}
                        />
                      }
                      label={
                        <strong>
                          {wixStatus === 'token_with_code' ? 'Deactivate Chatbot' : 'Make Chatbot Live'}
                        </strong>
                      }
                    />
                  </Box>
                )}
              </div>
            )}

            {tab === 2 && (
              <div className="webflow-tab">
                <WebflowSitePanel
                  botId={botId}
                  status={webflowStatus}
                  state={webflowState}
                  site={webflowSiteRow}
                  sites={sites}
                  selectedSiteId={site?.id || ''}
                  onSelectSite={(id) => { const x = sites.find((y) => y.id === id); if (x) setSite({ id: x.id, name: x.name }); }}
                  busy={loading}
                  disconnectedSites={webflowDisconnected}
                  onConnect={handleWebflowConnect}
                  onDisconnect={handleRemoveWebflow}
                  onToggle={(on) => (on ? handleActivateWebflowChatbot() : handleDeactivateWebflowChatbot())}
                  onPublished={() => setTimeout(fetchWebflowStatus, 1500)}
                  onRetry={fetchWebflowStatus}
                />
              </div>
            )}

            {tab === 3 && (
              <div className="wordpress-tab-c">
                <button className="wp-btn-c left" onClick={handleCopyBotId}>
                  <FontAwesomeIcon icon={copiedId ? faCircleCheck : faClone} />
                  <span>{copiedId ? 'Bot ID copied' : 'Copy Bot ID'}</span>
                </button>

                <button
                  className="wp-btn-c right"
                  onClick={() =>
                    window.open(
                      process.env.REACT_APP_WP_PLUGIN_URL || 'https://wordpress.org/plugins/ultimo-bots/',
                      '_blank'
                    )
                  }
                >
                  <FontAwesomeIcon icon={faArrowDown} />
                  <span>Install plugin</span>
                </button>
              </div>
            )}
          </>
        </div>
      </Box>
    </Modal>
  );
};

export default IntegrationModal;
